/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AdElement, AdElementVector } from '@adflow/types';

/**
 * The background picture and widget placeholders are always imported: a widget's artwork is
 * removed from the background only when the widget layer is imported.
 */
function isAlwaysImported(el: AdElement): boolean {
  return el.type === 'SVG' || el.type === 'WIDGET';
}

function mapDataPointToTeamAssetSource(
  dataPointName: string,
): 'HOME_ASSET' | 'AWAY_ASSET' | 'COMPETITOR_ASSET' | 'OPPONENT_ASSET' | undefined {
  switch (dataPointName) {
    case 'HOME_JERSEY':
    case 'HOME_BADGE':
      return 'HOME_ASSET';
    case 'AWAY_JERSEY':
    case 'AWAY_BADGE':
      return 'AWAY_ASSET';
    case 'COMPETITOR_JERSEY':
    case 'COMPETITOR_BADGE':
      return 'COMPETITOR_ASSET';
    case 'OPPONENT_JERSEY':
    case 'OPPONENT_BADGE':
      return 'OPPONENT_ASSET';
    default:
      return undefined;
  }
}

function mapDataPointToCasinoAssetSource(
  dataPointName: string,
): 'SQUARE_IMAGE' | 'LANDSCAPE_IMAGE' | 'PORTRAIT_IMAGE' | undefined {
  if (
    dataPointName === 'SQUARE_IMAGE' ||
    dataPointName === 'LANDSCAPE_IMAGE' ||
    dataPointName === 'PORTRAIT_IMAGE'
  ) {
    return dataPointName;
  }

  return undefined;
}

function mapDataPointToDisplayAssetType(dataPointName: string): 'JERSEY' | 'BADGE' | undefined {
  switch (dataPointName) {
    case 'HOME_JERSEY':
    case 'AWAY_JERSEY':
    case 'COMPETITOR_JERSEY':
    case 'OPPONENT_JERSEY':
      return 'JERSEY';
    case 'HOME_BADGE':
    case 'AWAY_BADGE':
    case 'COMPETITOR_BADGE':
    case 'OPPONENT_BADGE':
      return 'BADGE';
    default:
      return undefined;
  }
}

const TEAM_ASSET_DATA_POINTS = [
  'HOME_JERSEY',
  'AWAY_JERSEY',
  'COMPETITOR_JERSEY',
  'OPPONENT_JERSEY',
  'HOME_BADGE',
  'AWAY_BADGE',
  'COMPETITOR_BADGE',
  'OPPONENT_BADGE',
] as const;

const isTeamAssetDataPoint = (dataPointName: string): boolean => {
  return TEAM_ASSET_DATA_POINTS.indexOf(dataPointName as any) !== -1;
};

/**
 * This function takes in an AdElementVector and a string that contains a svg.
 * This svg contains all AdElements. We extract the Vector element and
 * effect filters. We also get the width and height from it and set it on the AdElementVector.
 * @param vectorElement
 * @param svgString
 * @returns AdElementVector with content, effectFilters and size(width, height) extracted from the svgString
 */
function mapVectorElement(vectorElement: AdElementVector, svgString: string): AdElementVector {
  const parser = new DOMParser();

  const svgDocument = parser.parseFromString(svgString, 'image/svg+xml');
  const vectorElementDoc = svgDocument.querySelector(
    '[id="' + vectorElement.name.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
  );

  if (!vectorElementDoc) {
    return vectorElement;
  }
  const { width, height } = getWidthAndHeightFromSVGElement(svgDocument, vectorElement);

  const filterStr = getFilterStringFromSVGElement(svgDocument, vectorElement);
  return {
    ...vectorElement,
    data: {
      ...vectorElement.data,
      effectFilters: filterStr,
      content: vectorElementDoc.outerHTML,
      viewBox: {
        width,
        height,
      },
    },
  };
}

function getWidthAndHeightFromSVGElement(svgDocument: Document, vectorElement: AdElementVector) {
  const width =
    parseFloat(svgDocument.querySelector('svg')?.getAttribute('width') || '') ||
    vectorElement.size.width;
  const height =
    parseFloat(svgDocument.querySelector('svg')?.getAttribute('height') || '') ||
    vectorElement.size.height;
  return { width, height };
}

function getFilterStringFromSVGElement(svgDocument: Document, vectorElement: AdElementVector) {
  const groupElement = svgDocument.querySelector(
    '[id="' + vectorElement.name.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
  );
  let filterStr = '';
  let filterId = '';
  filterId = groupElement?.getAttribute('filter')?.replace('url(#', '').replace(')', '') as string;
  let filterElement;
  if (filterId) {
    filterElement = svgDocument.querySelector(
      '[id="' + filterId.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
    );
  }
  const serializer = new XMLSerializer();
  if (filterElement) {
    filterStr = serializer.serializeToString(filterElement);
  }
  return filterStr;
}

export const elementSelectionHelper = {
  isAlwaysImported,
  mapDataPointToCasinoAssetSource,
  mapDataPointToTeamAssetSource,
  mapDataPointToDisplayAssetType,
  isTeamAssetDataPoint,
  mapVectorElement,
};
